
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';
import { SubscriptionHandlerDirective } from '../../directives/subscription-handler.directive';
import { FormField, FormDTO, FormRequest } from '../../models/form.model';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ChatService } from '../../services/chat.service';
import { formFieldTypeValidations, formGroupValidations, noDuplicateZoneNamesValidator, zoneDependentValidator } from '../../const/form-validations';
import { distinctUntilChanged } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';
import { FormControlType, FormFieldDataType } from '../../enums/form.enum';

@Component({
    selector: 'app-chat-form-input',
    templateUrl: './chat-form-input.component.html',
    styleUrls: ['./chat-form-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatFormInputComponent extends SubscriptionHandlerDirective implements OnChanges {
    @Input() formRequest: FormRequest;
    @Input() isReadOnly: boolean = false;
    @Input() isMobileApp: boolean = false;
    @Input() isCCU: boolean = false;
    @Output() openMobileInAppBrowser = new EventEmitter();
    showSelectPopupForMobile: boolean = false;
    activeSelectFieldId: string = '';
    fieldSubscriptions: { [key: string]: any } = {};
    private isFormCompleted: boolean = false;

    form!: FormGroup;
    constructor(
        private readonly chatService: ChatService
    ) {
        super();
    }

    ngOnInit() {
        this.onEdit();
        this.trackFormDataChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const readOnlyChange = changes['isReadOnly'];
        if (readOnlyChange && !readOnlyChange.firstChange && !readOnlyChange.previousValue && readOnlyChange.currentValue && !this.isFormCompleted) {
            this.resetFormValues();
        }
    }

    onEdit() {
        this.form = this.toFormGroup(this.formRequest.form);
    }

    /**
     * Clears unsubmitted selections when the form moves to read-only mode without being submitted
     */
    resetFormValues(): void {
        this.formRequest?.form?.fields?.forEach(field => {
            const emptyValue = Array.isArray(field.value) || field.controlType === FormControlType.CHECKBOX ? [] : '';
            field.value = emptyValue as any;
            field.displayValue = '';
            field.error = undefined;
            this.form?.get(field.id)?.reset(emptyValue, { emitEvent: false });
        });
        Object.values(this.fieldSubscriptions).forEach(sub => sub?.unsubscribe?.());
        this.fieldSubscriptions = {};
        this.showSelectPopupForMobile = false;
        this.activeSelectFieldId = '';
    }

    private _loader: boolean = false;

    set loader(value: boolean) {
        value ? this.form.disable() : this.form.enable();
        this._loader = value;
    }

    get loader(): boolean {
        return this._loader;
    }

    getFieldsByGroupId(groupId: string) {
        return this.formRequest.form.fields.filter(field => field.groupId === groupId);
    }

    checkGroupFieldsNotEmpty(groupId: string): boolean {
        const fields = this.getFieldsByGroupId(groupId);
        return fields.some(field => {
            const control = this.form.get(field.id);
            return control && control.value !== null && control.value !== undefined && control.value !== '';
        });
    }

    hasGroups() {
        return this.formRequest.form.groups && this.formRequest.form.groups.length > 0;
    }

    private normalizeValue(value: any): string {
        return value?.toString().toLowerCase() ?? '';
    }

    /*
        Returns the display label for a given form field and its allowed values.
        Handles both single and multiple value fields.
    */
    getDisplayLabel(field: FormField, allowedValues: any[]): string {
        const value = field.value;
        if (!allowedValues) return value as string;
        // This check is needed when the form field stores multiple values
        if (Array.isArray(value)) {
            if (!value.length) return '';
            return value.map(v => this.getDisplayLabel({ ...field, value: v }, allowedValues)).join(', ');
        }
        // For single value
        if (!value && !field.displayValue) return '';
        const option = allowedValues.find(opt => opt.value === value);
        return option ? option.label : field.displayValue ?? value;
    }

    getDisplayLabelForMultiSelect(value: string | string[], optionGroups: any[]): { label: string, caption: string }[] {
        if (!value || !Array.isArray(value) || !optionGroups) {
            return [];
        }
        const allowedValues = optionGroups.map(group => group.allowedValues).flat();
        return value.map(val => {
            const match = allowedValues.find(item => item.value === val);
            if (match) {
                return { label: match.label, caption: match.caption };
            } else {
                return { label: val, caption: '' };
            }
        });
    }

    trackById(item: any): string {
        return item?.id;
    }

    isCheckboxSelected(fieldId: string, optionValue: string): boolean {
        const control = this.form.get(fieldId);
        if (!control) return false;
        
        const value = control.value;
        return Array.isArray(value) && value.includes(optionValue);
    }

    handleCheckboxChange(fieldId: string, optionValue: string, event: any, optGroup?: any): void {
      const control = this.form.get(fieldId);
      if (!control) return;
      
      const isChecked = event.target.checked;
      let currentValues = control.value ?? [];
      if (!Array.isArray(currentValues)) {
        currentValues = [];
      }

      if (optGroup && optGroup.dataType == FormFieldDataType.SINGLE_STATE) {
        // Remove all values that belong to this group
        const otherValues = currentValues.filter((v: any) => !optGroup.allowedValues.some((opt: any) => opt.value === v));
        // Add the newly selected value if checked
        currentValues = isChecked ? [...otherValues, optionValue] : [...otherValues];
      } else {
        if (isChecked && !currentValues.includes(optionValue)) {
          currentValues.push(optionValue);
        } else if (!isChecked && currentValues.includes(optionValue)) {
          currentValues = currentValues.filter((v: any) => v !== optionValue);
        }
      }
      control.setValue(currentValues);
    }

    onSubmit() {
        const request = this.fromFormGroup();

        this.loader = true;
        this.chatService.submitUserFormIntake(this.formRequest.taskType, this.formRequest.taskId, request, true).subscribe((response) => {
            const hasError = response.form.fields.some(field => field.hasOwnProperty('error') && field.error);
            if (hasError) {
                this.loader = false;
                this.checkServerValidations(response);
                return;
            } else {
                // Update the form contents with the response
                this.formRequest = response;
                this.isFormCompleted = true;
                this.isReadOnly = true;
                this.loader = false;
            }
        }, (error) => {
            console.error(error);
            this.loader = false;
        });
    }

    /**
     * Maps the DTO to the form object
     * @param fields 
     * @returns Form Request
     */
    toFormGroup(formData: FormDTO): FormGroup {
        this.clearSubscriptions();
        const controls: { [key: string]: FormControl } = {};
        formData.fields.forEach(field => {
            if (field.controlType === FormControlType.SELECT) {
                const currentValue = this.normalizeValue(field.value);
                if (field.allowedValues?.length) {
                    const matchedOption = field.allowedValues.find(item =>
                        this.normalizeValue(item.value) === currentValue
                    );
                    if (matchedOption) {
                        field.value = matchedOption.value;
                    } else {
                        field.value = '';
                    }
                }
            }
            
            // Handle AUTOCOMPLETE control type by matching the current value with allowed options and setting displayValue accordingly
            if (field.controlType === FormControlType.AUTOCOMPLETE) {
                const currentValue = this.normalizeValue(field.value);
                if (field.allowedValues?.length) {
                    const matchedOption = field.allowedValues.find(item =>
                        this.normalizeValue(item.value) === currentValue
                    );
                    
                    if (matchedOption) {
                        field.value = matchedOption.value;
                        field.displayValue = matchedOption.label ?? '';
                    } else {
                        field.value = '';
                        field.displayValue = field?.displayValue ?? '';
                    }
                }
            }
            
            controls[field.id] = new FormControl(field.value ?? '', this.updateFormValidators(field));
            
            // Marking fields as touched if is required and in edit mode to show validation errors
            if (formData.isEditMode && field.isRequired) {
                controls[field.id].markAsTouched();
            }

            // Disable and handle "same as other field" functionality
            if (field?.autoCopyEnabled) {
                const autoCopy = field.presentationData?.autoCopy;
                if (autoCopy?.autoCopyValue) {
                    field.disabled = true;
                    const dependentFieldControl = controls[autoCopy.sameAsFieldId];
                    if (dependentFieldControl) {
                        controls[field.id].setValue(dependentFieldControl.value);
                        // Subscribe to dependent control changes
                        this.fieldSubscriptions[field.id] = dependentFieldControl.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => {
                            controls[field.id].setValue(val);
                        });
                    }
                }
            }
        });
    
        //Conditionally apply group-based validators (if groups exist)
        if (formData.groups?.length) {
            formData.groups.forEach(group => {

                const groupFields = formData.fields.filter(field => field.groupId === group.id);

                if (groupFields.length && group.validationKeys?.length) {
                   
                    const groupControls: { [key: string]: FormControl } = {};
                    groupFields.forEach(field => {
                        groupControls[field.id] = controls[field.id];
                    });

                    const tempGroup = new FormGroup(groupControls);
                    const validators: ValidatorFn[] = [];
                    // Add group-level validators based on the validation keys
                    const validationMappings = [
                        { key: formGroupValidations.ZoneDependent, validator: zoneDependentValidator },
                        { key: formGroupValidations.ZoneDuplicate, validator: noDuplicateZoneNamesValidator }
                    ];
                    validationMappings.forEach(({ key, validator }) => {
                        const metaData = group.presentationData?.[key];
                        if (group.validationKeys?.includes(key) && metaData) {
                            validators.push(validator(metaData));
                        }
                    });
                    if (validators.length) {
                        tempGroup.setValidators(Validators.compose(validators));
                    }
                    // Subscribe to any control change to trigger tempGroup validation
                    Object.values(groupControls).forEach(control => {
                        control.valueChanges.subscribe(() => {
                            tempGroup.updateValueAndValidity({ onlySelf: true });
                        });
                    });
                }
            });
        }    
        return new FormGroup(controls);
    }

    get formDescription(): string | null {
        return this.formRequest?.description ?? '';
    }

    get headerHtml(): string | null {
        return this.formRequest.form.headerHtml ?? '';
    }

    get footerHtml(): string | null {
        return this.formRequest.form.footerHtml ?? '';
    }

    /**
     * Remove field from the form
     * @param fieldId 
     */
    removeField(fieldId: string): void {
        this.formRequest.form.fields = this.formRequest.form.fields.filter(field => field.id !== fieldId);
        this.form.removeControl(fieldId);
    }

    /**
     * Updates the request with form data
     * @returns Updated FormRequest object
     */
    fromFormGroup(): FormDTO {
        const fields: FormField[] = [];
        this.formRequest.form.fields.forEach(field => {
            const control = this.form.controls[field.id];
            fields.push({
                ...field,
                value: (field.controlType == FormControlType.INPUT && typeof control.value === 'string') ? control.value.trim() : control.value,
            });
        });
        return {
            ...this.formRequest.form,
            fields: fields
        }
    }

    onCancel() {
        this.loader = true;
        this.chatService.cancelUserFormIntake(this.formRequest.taskType, this.formRequest.taskId).subscribe((response) => {
            this.formRequest.description = response.content as string;
            this.formRequest.form.fields = [];
            this.loader = false;
            this.isFormCompleted = true;
            this.isReadOnly = true;
        });
    }

    updateFormValueFromDropdown(selectedValue: any, field: FormField) {
        if (selectedValue !== null) {
            if (field.allowedValues?.length) {
                const matchedOption = field.allowedValues.find(item =>
                    this.normalizeValue(item.value) === this.normalizeValue(selectedValue)
                );
                if (matchedOption) {
                    this.form.get(field?.id)?.setValue(matchedOption.value);
                    field.value = matchedOption.value;
                }
            }
        }
        setTimeout(() => {
            this.showSelectPopupForMobile = false;
            this.activeSelectFieldId = '';
        }, 0);
    }

    /**
     * Updates the form value for an autocomplete field.
     * @param selectedValue The selected value from the autocomplete modal.
     * @param field The form field being updated.
     */
    updateFormValueFromAutocomplete(selectedValue: any, field: FormField) {
        if (selectedValue !== null) {
            if (selectedValue?.isCreated && field.presentationData?.allowCreate) {
                this.form.get(field?.id)?.setValue('');
                field.value = '';
                field.displayValue = selectedValue.label ?? '';
            } else if (field.allowedValues?.length) {
                const matchedOption = field.allowedValues.find(item =>
                    this.normalizeValue(item.value) === this.normalizeValue(selectedValue)
                );
                if (matchedOption) {
                    this.form.get(field?.id)?.setValue(matchedOption.value);
                    field.value = matchedOption.value;
                    field.displayValue = matchedOption.label ?? '';
                }
            }
        }
    }

    isFormFieldNotEmpty(field: FormField): boolean {
        if (field.controlType === FormControlType.AUTOCOMPLETE) {
            return !!field.value || !!field.displayValue;
        }

        return field.value !== null && field.value !== undefined && field.value !== '';
    }

    private autocompleteRequiredValidator(field: FormField): ValidatorFn {
        return (_control: AbstractControl) => {
            const displayValue = field.displayValue?.trim() ?? '';
            return displayValue ? null : { required: true };
        };
    }

    updateFormValidators(field: FormField) {
        const isAutocompleteField = field.controlType === FormControlType.AUTOCOMPLETE;
        const validators = field.isRequired
            ? [isAutocompleteField ? this.autocompleteRequiredValidator(field) : Validators.required]
            : [];
        if (field?.validationKeys && field.validationKeys.length > 0) {
            field.validationKeys?.forEach((validationKey) => {
                const validationConfig =
                    formFieldTypeValidations[
                    validationKey as keyof typeof formFieldTypeValidations
                    ];
                if (validationConfig?.validator) {
                    if (isAutocompleteField) {
                        validators.push((_control: AbstractControl) => {
                            const displayValue = field.displayValue ?? '';
                            return validationConfig.validator(new FormControl(displayValue));
                        });
                    } else {
                        validators.push(validationConfig.validator);
                    }
                }

                // Max Length validation check
                if (validationConfig?.maxLength) {
                    field.maxLength = validationConfig.maxLength;
                }
            });
        }
        return validators;
    }

    checkValidations(field: FormField) {
        const control = this.form.get(field.id);
        if (!control) return false;
        control.updateValueAndValidity({ onlySelf: true });
        if (field.hasOwnProperty('error') && field.error) {
            control.setErrors({ serverError: field.error });
        }
        if (!control?.errors) return false;

        // If there's a required error, check if the control is dirty or touched
        if (control.errors['required']) {
            return control.dirty || control.touched;
        }
        return control?.errors ? Object.keys(control.errors).length > 0 : false;
    }

    generateTooltipForFormField(field: FormField): string {
        const control = this.form.get(field.id);
        if (control && control?.errors) {
            // Check standard required error
            if (control.hasError('required')) {
                return `${field.label} is required`;
            }
            const matchedKey = (field.validationKeys || []).find(key => {
                const config = formFieldTypeValidations[key as keyof typeof formFieldTypeValidations];
                return config && control.hasError(config.errorKey);
            });
            if (matchedKey) {
                const { message } = formFieldTypeValidations[matchedKey as keyof typeof formFieldTypeValidations];
                return message();
            } else {
                const customError = control.errors ? Object.values(control.errors).find(err => typeof err === 'string') : null;
                return customError || ''
            }
        }
        return '';
    }

    trackFormDataChanges() {
        for (const [key, control] of Object.entries(this.form.controls)) {
            control.valueChanges
                .pipe(distinctUntilChanged())
                .subscribe(_ => {
                    const field = this.formRequest.form.fields.find(f => f.id === key);
                    if (field && field?.presentationData && field?.presentationData['isConfidenceHigh'] == false && control.dirty) {
                        // Making the confidence high if the field is updated by the user
                        field.presentationData['isConfidenceHigh'] = true;
                    }

                    if (field && field?.error) {
                         // Clear server errors if the field is updated by the user
                        control.setErrors(null);
                        control.updateValueAndValidity({ onlySelf: true });
                        field.error = undefined;
                    }
                });
        }
    }

    // Show tooltip for mobile devices on click event
    showTooltipForMobile(tooltipRef: MatTooltip) {
        setTimeout(() => {
            tooltipRef.show();
            setTimeout(() => {
                tooltipRef.hide();
            }, 1500);
        }, 0);
    }

    checkServerValidations(formFields: FormRequest) {
        formFields.form.fields.forEach((field: FormField) => {
            const control = this.form.get(field.id);
            const matchedField = this.formRequest.form.fields.find((f: FormField) => f.id === field.id);
            if (matchedField) {
                matchedField.error = field.error;
            }
            if (field.hasOwnProperty('error') && field.error) {
                const error = field.error;
                if (control) {
                    control.setErrors({ serverError: error });
                }
            } else if (control) {
                control.setErrors(null); // Clear errors if no server error exists
            }
        });
    }

    sameAsCheckboxSelection(event: Event, sameAsFieldId: string, field: any) {
        const isChecked = (event.target as HTMLInputElement).checked;
        // Get the dependent control and the current field control
        const dependentControl = this.form.get(sameAsFieldId);
        const currentControl = this.form.get(field.id);

        if (field?.autoCopyEnabled && field.presentationData && field.presentationData.autoCopy) {
            field.presentationData.autoCopy.autoCopyValue = isChecked;
        } else {
            return;
        }
        // Unsubscribe previous subscription if exists
        if (this.fieldSubscriptions[field.id]) {
            this.fieldSubscriptions[field.id].unsubscribe();
        }

        if (isChecked) {
            if (dependentControl && currentControl) {
                currentControl.setValue(dependentControl.value);

                // Subscribe to dependent control changes
                this.fieldSubscriptions[field.id] = dependentControl.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => {
                    currentControl.setValue(val);
                });
            }
            field.disabled = true;
        } else {
            field.disabled = false;
        }
    }

    // Opens the mobile in-app browser for specific links
    openInAppBrowser(url: string): void {
        if (this.isMobileApp) {
            this.openMobileInAppBrowser.emit({ from: 'citation', url });
        }
    }

    override ngOnDestroy(): void {
        // Unsubscribe all field-level subscriptions
        this.clearSubscriptions();

        // Call base class cleanup
        super.ngOnDestroy();
    }

    clearSubscriptions(): void {
        // Unsubscribe all field-level subscriptions
        Object.values(this.fieldSubscriptions).forEach(sub => {
            if (sub) {
                sub.unsubscribe();
            }
        });
    }
}
